import * as tslib_1 from "tslib";
/** @jsx jsx */

import { jsx, css } from "@emotion/core";
import * as React from "react";
import { Text } from "./Text";
import VisuallyHidden from "@reach/visually-hidden";
import PropTypes from "prop-types";
import { alpha } from "./Theme/colors";
import { useUid } from "./Hooks/use-uid";
import { useTheme } from "./Theme/Providers";
import { getHeight, focusShadow } from "./Button";
import { IconAlertCircle, IconChevronDown } from "./Icons";

var getInputSizes = function getInputSizes(theme) {
  return {
    sm: css({
      fontSize: theme.fontSizes[0],
      padding: "0.25rem 0.5rem"
    }),
    md: css({
      fontSize: theme.fontSizes[1],
      padding: "0.5rem 0.75rem"
    }),
    lg: css({
      fontSize: theme.fontSizes[2],
      padding: "0.65rem 1rem"
    })
  };
};

export var InputGroup = function InputGroup(_a) {
  var id = _a.id,
      label = _a.label,
      children = _a.children,
      error = _a.error,
      helpText = _a.helpText,
      hideLabel = _a.hideLabel,
      other = tslib_1.__rest(_a, ["id", "label", "children", "error", "helpText", "hideLabel"]);

  var uid = useUid(id);
  var theme = useTheme();
  var isDark = theme.colors.mode === "dark";
  var danger = isDark ? theme.colors.intent.danger.light : theme.colors.intent.danger.base;
  return jsx("div", tslib_1.__assign({
    className: "InputGroup",
    css: {
      marginTop: theme.spaces.md,
      ":first-child": {
        marginTop: 0
      }
    }
  }, other), jsx(Label, {
    hide: hideLabel,
    htmlFor: uid
  }, label), React.isValidElement(children) && React.cloneElement(children, {
    id: uid
  }), error && typeof error === "string" ? jsx("div", {
    className: "InputGroup__error",
    css: {
      alignItems: "center",
      marginTop: theme.spaces.sm,
      display: "flex"
    }
  }, jsx(IconAlertCircle, {
    size: "sm",
    color: danger
  }), jsx(Text, {
    css: {
      display: "block",
      marginLeft: theme.spaces.xs,
      fontSize: theme.fontSizes[0],
      color: danger
    }
  }, error)) : error, helpText && jsx(Text, {
    className: "InputGroup__help",
    css: {
      display: "block",
      marginTop: theme.spaces.xs,
      color: theme.colors.text.muted,
      fontSize: theme.fontSizes[0]
    },
    variant: "body"
  }, helpText));
};
InputGroup.propTypes = {
  label: PropTypes.string.isRequired,
  hideLabel: PropTypes.bool,
  helpText: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

function getBaseStyles(theme) {
  var dark = theme.colors.mode === "dark";
  var gray = dark ? theme.colors.palette.gray.light : theme.colors.palette.gray.base;
  var baseStyles = css({
    display: "block",
    width: "100%",
    lineHeight: theme.lineHeights.body,
    color: theme.colors.text.default,
    backgroundColor: "transparent",
    backgroundImage: "none",
    backgroundClip: "padding-box",
    WebkitFontSmoothing: "antialiased",
    WebkitTapHighlightColor: "transparent",
    WebkitAppearance: "none",
    boxSizing: "border-box",
    touchAction: "manipulation",
    fontFamily: theme.fonts.base,
    border: "none",
    boxShadow: "0 0 0 2px transparent inset, 0 0 0 1px " + (dark ? alpha(theme.colors.palette.gray.lightest, 0.14) : alpha(theme.colors.palette.gray.dark, 0.2)) + " inset",
    borderRadius: theme.radii.sm,
    transition: "background 0.25s cubic-bezier(0.35,0,0.25,1), border-color 0.15s cubic-bezier(0.35,0,0.25,1), box-shadow 0.15s cubic-bezier(0.35,0,0.25,1)",
    "::placeholder": {
      color: alpha(theme.colors.text.default, 0.45)
    },
    ":focus": {
      boxShadow: dark ? focusShadow(alpha(theme.colors.palette.blue.light, 0.5), alpha(theme.colors.palette.gray.dark, 0.4), alpha(theme.colors.palette.gray.light, 0.2)) : focusShadow(alpha(theme.colors.palette.blue.dark, 0.1), alpha(theme.colors.palette.gray.dark, 0.2), alpha(theme.colors.palette.gray.dark, 0.05)),
      outline: "none"
    },
    ":disabled": {
      boxShadow: "inset 0 0 0 1px " + alpha(gray, 0.45)
    },
    ":active": {
      background: theme.colors.background.tint1
    }
  });
  return baseStyles;
}

function useActiveStyle() {
  var _a = React.useState(false),
      active = _a[0],
      setActive = _a[1];

  return {
    bind: {
      onTouchStart: function onTouchStart() {
        return setActive(true);
      },
      onTouchEnd: function onTouchEnd() {
        return setActive(false);
      }
    },
    active: active
  };
}

function useSharedStyle() {
  var theme = useTheme();
  var baseStyles = React.useMemo(function () {
    return getBaseStyles(theme);
  }, [theme]);
  var inputSizes = React.useMemo(function () {
    return getInputSizes(theme);
  }, [theme]);
  var activeBackground = css({
    background: theme.colors.background.tint1
  });
  return {
    baseStyles: baseStyles,
    inputSizes: inputSizes,
    activeBackground: activeBackground
  };
}
/**
 * Our basic Input element. Use this when building customized
 * forms. Otherwise, stick with InputGroup
 */


export var InputBase = function InputBase(_a) {
  var autoComplete = _a.autoComplete,
      autoFocus = _a.autoFocus,
      _b = _a.inputSize,
      inputSize = _b === void 0 ? "md" : _b,
      other = tslib_1.__rest(_a, ["autoComplete", "autoFocus", "inputSize"]);

  var _c = useActiveStyle(),
      bind = _c.bind,
      active = _c.active;

  var _d = useSharedStyle(),
      baseStyles = _d.baseStyles,
      inputSizes = _d.inputSizes,
      activeBackground = _d.activeBackground;

  var height = getHeight(inputSize);
  return jsx("input", tslib_1.__assign({
    className: "Input",
    autoComplete: autoComplete,
    autoFocus: autoFocus
  }, bind, {
    css: [baseStyles, inputSizes[inputSize], active && activeBackground, {
      height: height
    }]
  }, other));
};
InputBase.propTypes = {
  inputSize: PropTypes.oneOf(["sm", "md", "lg"])
};
export var Input = InputBase;
/**
 * Textarea version of InputBase
 */

export var TextArea = function TextArea(_a) {
  var _b = _a.inputSize,
      inputSize = _b === void 0 ? "md" : _b,
      other = tslib_1.__rest(_a, ["inputSize"]);

  var _c = useActiveStyle(),
      bind = _c.bind,
      active = _c.active;

  var _d = useSharedStyle(),
      baseStyles = _d.baseStyles,
      inputSizes = _d.inputSizes,
      activeBackground = _d.activeBackground;

  return jsx("textarea", tslib_1.__assign({
    className: "TextArea"
  }, bind, {
    css: [baseStyles, inputSizes[inputSize], {
      overflow: "auto",
      resize: "vertical"
    }, active && activeBackground]
  }, other));
};
TextArea.propTypes = {
  inputSize: PropTypes.oneOf(["sm", "md", "lg"])
};
/**
 * A styled Label to go along with input elements
 */

export var Label = function Label(_a) {
  var children = _a.children,
      hide = _a.hide,
      other = tslib_1.__rest(_a, ["children", "hide"]);

  var theme = useTheme();
  var child = jsx("label", tslib_1.__assign({
    className: "Label",
    css: {
      display: "inline-block",
      marginBottom: hide ? 0 : theme.spaces.sm
    }
  }, other), jsx(Text, {
    className: "Label__text",
    variant: "subtitle"
  }, children));
  return hide ? jsx(VisuallyHidden, null, child) : child;
};
Label.propTypes = {
  hide: PropTypes.bool
};
/**
 * A styled select menu
 */

export var Select = function Select(_a) {
  var multiple = _a.multiple,
      _b = _a.inputSize,
      inputSize = _b === void 0 ? "md" : _b,
      other = tslib_1.__rest(_a, ["multiple", "inputSize"]);

  var theme = useTheme();
  var inputSizes = getInputSizes(theme);
  var selectSize = {
    sm: inputSizes.sm,
    md: inputSizes.md,
    lg: inputSizes.lg
  };
  var dark = theme.colors.mode === "dark";
  var height = getHeight(inputSize);
  return jsx("div", {
    className: "Select",
    css: {
      position: "relative"
    }
  }, jsx("select", tslib_1.__assign({
    className: "Select__input",
    css: [selectSize[inputSize], {
      WebkitAppearance: "none",
      display: "block",
      width: "100%",
      lineHeight: theme.lineHeights.body,
      height: height,
      color: theme.colors.text.default,
      background: "transparent",
      fontFamily: theme.fonts.base,
      boxShadow: "0 0 0 2px transparent inset, 0 0 0 1px " + (dark ? alpha(theme.colors.palette.gray.lightest, 0.14) : alpha(theme.colors.palette.gray.dark, 0.2)) + " inset",
      border: "none",
      backgroundClip: "padding-box",
      borderRadius: theme.radii.sm,
      margin: 0,
      "& [disabled]": {
        opacity: 0.8,
        backgroundImage: "none",
        cursor: "not-allowed"
      },
      ":focus": {
        borderColor: theme.colors.palette.blue.base,
        boxShadow: dark ? focusShadow(alpha(theme.colors.palette.blue.light, 0.5), alpha(theme.colors.palette.gray.dark, 0.4), alpha(theme.colors.palette.gray.light, 0.2)) : focusShadow(alpha(theme.colors.palette.blue.dark, 0.1), alpha(theme.colors.palette.gray.dark, 0.2), alpha(theme.colors.palette.gray.dark, 0.05)),
        outline: 0
      }
    }],
    multiple: multiple
  }, other)), !multiple && jsx(IconChevronDown, {
    className: "Select__icon",
    color: theme.colors.text.muted,
    css: {
      position: "absolute",
      top: "50%",
      right: "0.75rem",
      transform: "translateY(-50%)"
    }
  }));
};
Select.propTypes = {
  inputSize: PropTypes.oneOf(["sm", "md", "lg"])
};
export var Check = function Check(_a) {
  var label = _a.label,
      id = _a.id,
      other = tslib_1.__rest(_a, ["label", "id"]);

  var uid = useUid(id);
  var theme = useTheme();
  return jsx("div", tslib_1.__assign({
    className: "Check",
    css: {
      display: "flex",
      alignItems: "center"
    }
  }, other), jsx("input", tslib_1.__assign({
    className: "Check__input",
    type: "checkbox",
    id: uid
  }, other)), jsx("label", {
    className: "Check__label",
    css: {
      marginLeft: theme.spaces.xs
    },
    htmlFor: uid
  }, jsx(Text, null, label)));
};
Check.propTypes = {
  label: PropTypes.string.isRequired
};